'use client';

import React, { useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useGetReferralsV2PaginatedInfiniteQuery } from 'src/api/redux/referrals/referralsApi';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { getSearchQueryParamsForAPI } from 'src/utils/url.helpers';
import { getProfileUrl } from 'src/utils/profileUrl';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { applyCaptureRate } from 'src/components/Profiles/shared/widgets/utils';

const PREVIEW_MAX_ITEMS = 2;
const PAGE_SIZE = 25;

const CompactOutboundReferralsPreview: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();
  const router = useRouter();
  const { closeDrawer } = useDrawer();

  const { providerId } = getEntityIdentifiers(entityDetails);
  const captureRate = widgetConfig?.widget?.capture_rate;
  const volumeFilterSetId = widgetConfig?.filterSet?.id;
  const volumeKey = volumeFilterSetId ? String(volumeFilterSetId) : null;
  const widgetFilterSetId = widgetConfig?.filterSetId;

  const queryParams = useMemo(() => {
    const params: Record<string, any> = {
      direction: 'outbound',
      page: 1,
      pageSize: PAGE_SIZE,
    };

    if (volumeKey) {
      params.sort = 'volume';
      params.sort_id = volumeKey;
      params.sort_field = 'patients';
      params.order = 'desc';
    } else {
      params.sort = 'total_patients';
      params.order = 'desc';
    }

    if (widgetFilterSetId) {
      params.filter_set = widgetFilterSetId;
    }

    return getSearchQueryParamsForAPI(params);
  }, [volumeKey, widgetFilterSetId]);

  const postBody = useMemo(() => {
    if (!volumeKey) return {};

    return {
      [volumeKey]: {
        code_set: widgetConfig?.widget?.code_set?.id,
        mode: 'receiving',
        type: 'patients',
        date_range: { gte: widgetConfig?.minYear, lte: widgetConfig?.maxYear },
        filter_sets: [],
        filters: [],
      },
    };
  }, [
    volumeKey,
    widgetConfig?.minYear,
    widgetConfig?.maxYear,
    widgetConfig?.widget?.code_set?.id,
  ]);

  const { data: paginatedData, isLoading } =
    useGetReferralsV2PaginatedInfiniteQuery(
      {
        providerId: String(providerId),
        queryParams,
        data: postBody,
      },
      { skip: !providerId }
    );

  const rows = useMemo(
    () =>
      (paginatedData?.pages?.flatMap((page: any) => page.results) ?? []).slice(
        0,
        PREVIEW_MAX_ITEMS
      ),
    [paginatedData]
  );

  const getPatientCount = (row: Record<string, any>) => {
    let value: number | null = null;
    if (volumeKey) {
      value = row.patients?.[volumeKey] != null ? Number(row.patients[volumeKey]) : null;
    } else {
      value = row.total_patients != null ? Number(row.total_patients) : null;
    }
    if (value == null) return '-';
    return applyCaptureRate(value, captureRate).toLocaleString();
  };

  const getClaimCount = (row: Record<string, any>) => {
    let value: number | null = null;
    if (volumeKey) {
      value = row.claims?.[volumeKey] != null ? Number(row.claims[volumeKey]) : null;
    } else {
      value = row.total_claims != null ? Number(row.total_claims) : null;
    }
    if (value == null) return '-';
    return applyCaptureRate(value, captureRate).toLocaleString();
  };

  const handleRowClick = useCallback(
    (row: Record<string, any>) => {
      const rowType = row.type || 'hcp';
      const rowId = row.provider_id || row.id;
      router.push(getProfileUrl(rowType, rowId));
      closeDrawer('profile');
    },
    [router, closeDrawer]
  );

  if (isLoading || widgetConfig?.isLoading) {
    return <WidgetLoading data-testid="compact-outbound-referrals-preview-loading" aria-label="Loading outbound referrals" />;
  }

  if (rows.length === 0) {
    return <WidgetEmpty data-testid="compact-outbound-referrals-preview-empty" />;
  }

  return (
    <div
      className="flex flex-col"
      data-testid="compact-outbound-referrals-preview"
      role="list"
      aria-label="Outbound Referrals"
    >
      {rows.map((row: Record<string, any>) => (
        <div
          key={row.provider_id}
          className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-b-0 cursor-pointer hover:bg-neutral-50 transition-colors"
          role="listitem"
          onClick={() => handleRowClick(row)}
        >
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-sm font-bold text-primary truncate">
              {row.name}
            </span>
            {row.specialty && (
              <span className="text-xs text-secondary truncate">
                {row.specialty}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center justify-end px-2 py-1 min-w-16">
              <span className="text-sm font-semibold text-teal-900 tabular-nums">
                {getPatientCount(row)}
              </span>
            </div>
            <div className="flex items-center justify-end px-2 py-1 min-w-16">
              <span className="text-sm font-semibold text-teal-900 tabular-nums">
                {getClaimCount(row)}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CompactOutboundReferralsPreview;
